import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/src/ui/layout.js";
import { Link } from 'gatsby';
import { createElement } from 'react';
import rehype2react from 'rehype-react';
import Doc from 'unified-doc';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3 {...{
      "id": "overview"
    }}>{`Overview`}</h3>
    <p>{`Parsers inform how content should be parsed into a valid `}<a parentName="p" {...{
        "href": "https://github.com/syntax-tree/hast"
      }}>{`hast`}</a>{` tree.  Parsers are assigned to their corresponding mime type (inferred from the `}<inlineCode parentName="p">{`filename`}</inlineCode>{`).  You can add a new parser, or override an existing parser, by assigning a custom parser to a mime type.`}</p>
    <h3 {...{
      "id": "supported-parsers"
    }}>{`Supported parsers`}</h3>
    <p><inlineCode parentName="p">{`unified-doc`}</inlineCode>{` infers the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types"
      }}>{`mime type`}</a>{` from the `}<inlineCode parentName="p">{`filename`}</inlineCode>{` to determine which parser should be used.  `}<inlineCode parentName="p">{`unified-doc`}</inlineCode>{` currently supports, and will eventually support, the following document formats (and relating mime types):`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": true,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`text/html`}</inlineCode>{`: HTML parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": true,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`text/markdown`}</inlineCode>{`: Markdown parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": true,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`text/csv`}</inlineCode>{`: CSV parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`application/vnd.openxmlformats-officedocument.wordprocessing`}</inlineCode>{`: DOCX parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`application/pdf`}</inlineCode>{`: PDF parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`application/x-latex`}</inlineCode>{`: LaTeX parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}<inlineCode parentName="li">{`application/rtf`}</inlineCode>{`: RTF parser`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`and potentially more formats.`}</li>
    </ul>
    <p>{`The `}<inlineCode parentName="p">{`filename`}</inlineCode>{` should be related to the `}<inlineCode parentName="p">{`content`}</inlineCode>{` intending to be parsed in a natural way.  This is usually not an issue if both pieces of information are read from a common source/file/document.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const markdownDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: '> **some** markdown content',
    filename: 'doc.md',
  });

  const htmlDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: '<blockquote><strong>some</strong> HTML content</blockquote>',
    filename: 'doc.html',
  });

  const csvDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: 'Column 0,Column 1,Column 2\\nrow1col0,row1col1,row1col2',
    filename: 'doc.csv',
  });

  return (
    <div>
      <h4><code>doc.md</code></h4>
      {markdownDoc.compile().result}
      <h4><code>doc.html</code></h4>
      {htmlDoc.compile().result}
      <h4><code>doc.csv</code></h4>
      {csvDoc.compile().result}
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "fallback-code-block-parser"
    }}>{`Fallback code block parser`}</h3>
    <p>{`If a parser is not supported from its inferred mime type, a fallback parser that renders content into a code block is applied.  This is useful for syntax highlighters, and will be further explored in the `}<Link to="/recipes/syntax-highlighting" mdxType="Link">{`Syntax highlighting`}</Link>{` section.  Source code documents usually fall under this category.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const jsonDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: JSON.stringify({
      one: 2,
      three: [true, false, null, 'four', 5],
    }, null, 2),
    filename: 'doc.json',
  });

  const jsDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: 'function greet() {\\n  return "hello world";\\n}',
    filename: 'doc.js',
  });

  const pythonDoc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: 'def greet():\\n\\treturn "hello world"',
    filename: 'doc.py',
  });

  return (
    <div>
      <h4><code>doc.json</code></h4>
      {jsonDoc.compile().result}
      <h4><code>doc.js</code></h4>
      {jsDoc.compile().result}
      <h4><code>doc.py</code></h4>
      {pythonDoc.compile().result}
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "custom-parser"
    }}>{`Custom parser`}</h3>
    <p>{`Parsers are applied using the `}<inlineCode parentName="p">{`PluggableList`}</inlineCode>{` interface and can include multiple steps e.g. `}<inlineCode parentName="p">{`[parser1]`}</inlineCode>{` or `}<inlineCode parentName="p">{`[parser2, parser3]`}</inlineCode>{`.  Custom parsers are specified through a mapping of mimeTypes to associated parsers.`}</p>
    <p>{`You can override an existing parser with a new parser by assigning to its supported mime type or assign specific parsers to unsupported mime types.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import Doc from 'unified-doc';

function () {
  function flattenToString() {
    this.Parser = function(doc) {
      return {
        type: 'root',
        children: [
          {
            type: 'text',
            value: doc,
          },
        ],
      };
    };
  };

  function capitalize() {
    return function transformer(tree) {
      const children = tree.children.map(child => ({
        ...child,
        value: child.value.toUpperCase(),
      }));
      return {
        ...tree,
        children,
      };
    };
  }

  const customParser = [flattenToString, capitalize];

  const markdownDoc = Doc({
    content: '> **some** markdown content',
    filename: 'doc.md',
    parsers: {
      'text/markdown': customParser,
      'application/json': customParser,
    },
  });

  const jsonDoc = Doc({
    content: '{ "one": 2, "three": [true, false, null, "four", 5]}',
    filename: 'doc.json',
    parsers: {
      'text/markdown': [flattenToString, capitalize],
      'application/json': [flattenToString, capitalize],
    },
  });

  return (
    <div>
      <h4>Override markdown parser</h4>
      <pre>{markdownDoc.compile().contents}</pre>
      <h4>New JSON parser</h4>
      <pre>{jsonDoc.compile().contents}</pre>
    </div>
  );
}
`}</code></pre>
    <h3 {...{
      "id": "disable-supported-parser"
    }}>{`Disable supported parser`}</h3>
    <p>{`If you would prefer to use the fallback code block parser to render the source code of the file, you can disable the parser by setting its value to `}<inlineCode parentName="p">{`null`}</inlineCode>{` for the associated mime type in the `}<inlineCode parentName="p">{`parsers`}</inlineCode>{` option.  The following shows how the default HTML parser can be disabled to rely on the fallback code block parser to render the source code of the HTML content.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "live=true",
        "live": "true"
      }}>{`// import { createElement } from 'react';
// import rehype2react from 'rehype-react';
// import Doc from 'unified-doc';

function() {
  const doc = Doc({
    compiler: [[rehype2react, { createElement }]],
    content: '<blockquote><strong>some</strong> HTML content</blockquote>',
    filename: 'doc.html',
    parsers: {
      'text/html': null,
    },
  });

  return <div>{doc.compile().result}</div>
}
`}</code></pre>
    {
      /* Definitions */
    }

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      